import {
  Box,
  Button,
  Grid,
  Pagination,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FormFilter } from "components/FormFilter";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IInscricao } from "shared/features/api/inscricao/types";
import { parseStatusEtapa } from "shared/utils/enumsParser";
import { IconButton, Modal } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { TrendingFlat } from "@mui/icons-material";

interface InscricoesEtapaListProps {
  inscricoes: IInscricao;
  etapa: string;
  trilha: string;
  setSearch: (valor: string) => void;
  page: number;
  setPage: (page: number) => void;
  statusEtapa: string;
  setStatusEtapa: (valor: string) => void;
  setTrilha: (valor: string) => void;
}

export const InscricoesEtapaList: React.FC<InscricoesEtapaListProps> = ({
  inscricoes,
  etapa,
  setSearch,
  page,
  setPage,
  statusEtapa,
  setStatusEtapa,
  trilha,
  setTrilha,
}) => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);

  const [sort, setSort] = useState("");
  const [order, setOrder] = useState(0);

  const cleanFilter = () => {
    setStatusEtapa("");
  };

  const columns = [
    {
      field: "nome",
      headerName: "Candidato",
      minWidth: 200,
      flex: 2,
      renderCell: (params: any) => {
        const nome = params.row.nome?.trim() || "";
        const email = params.row.email || "";

        return (
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            overflow="hidden"
          >
            <Box
              sx={{
                overflow: "hidden",
                minWidth: 0,
                flex: 1,
              }}
              title={`${nome}\n${email}`}
            >
              <Typography fontSize={14} noWrap>
                {nome}
              </Typography>
              <Typography fontSize={12} noWrap>
                {email}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "statusEtapa",
      headerName: "Status",
      minWidth: 160,
      flex: 1,
      renderCell: (params: any) => (
        <Box>
          <Typography fontSize={14}>{params.row.etapa}</Typography>
          <Typography fontSize={12}>{params.row.statusEtapa}</Typography>
        </Box>
      ),
    },
    {
      field: "primeiraOpcaoTrilha",
      headerName: "Trilhas (1° | 2°)",
      minWidth: 160,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        const primeira = params.row.primeiraOpcaoTrilha;
        const segunda = params.row.segundaOpcaoTrilha;
        return (
          <Box>
            <Typography fontSize={12}>
              {primeira} {primeira && segunda ? " | " : ""} {segunda}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "acoes",
      headerName: "Detalhes",
      width: 140,
      renderCell: (params: any) => (
        <Button
          variant="contained"
          onClick={() => {
            navigate("/candidatos/curriculo", { state: params.row });
          }}
        >
          Detalhes
        </Button>
      ),
    },
  ];

  const rows = (): any[] => {
    if (!inscricoes?.elementos || !Array.isArray(inscricoes.elementos))
      return [];

    return inscricoes.elementos
      .map((dados) => {
        const { inscricaoEtapas, candidato } = dados;

        if (!inscricaoEtapas || inscricaoEtapas.length === 0) return null;

        const etapaRow = inscricaoEtapas.find((e) => e.nomeEtapa === etapa);

        if (!etapaRow) return null;

        return {
          id: dados.idInscricao,
          nome: candidato?.nome ?? "-",
          email: candidato?.email ?? "-",
          primeiraOpcaoTrilha: dados.primeiraOpcaoTrilha ?? "-",
          segundaOpcaoTrilha: dados.segundaOpcaoTrilha ?? "-",
          statusEtapa: parseStatusEtapa(etapaRow.statusEtapa),
          etapa: etapaRow.nomeEtapa,
          edicao: dados.edicao ?? "-",
          telefone: candidato?.telefone ?? "-",
          turno: candidato?.formulario?.turno ?? "-",
          estado: candidato?.estado ?? "-",
          acoes: dados,
        };
      })
      .filter(Boolean);
  };

  return (
    <>
      <Paper
        sx={{
          height: { xs: "auto", md: "500px" },
          padding: { xs: 1, sm: 2 },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1, sm: 2 },
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            width: "100%",
            gap: { xs: 1, sm: 2 },
            mb: { xs: 1, sm: 0 },
          }}
        >
          <Box
            component="form"
            sx={{ width: "100%", mb: { xs: 1, sm: 0 }, height: "80%" }}
          >
            <TextField
              label="Pesquisar por nome ou email"
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
          <FormFilter
            statusEtapa={statusEtapa}
            setStatusEtapa={setStatusEtapa}
            etapa={etapa}
            trilha={trilha}
            setTrilha={setTrilha}
          />
          <Box display="flex" justifyContent="flex-end">
            <IconButton
              onClick={() => setOpenModal(true)}
              title="Expandir tabela"
            >
              <FullscreenIcon />
            </IconButton>
          </Box>
          <Button
            variant="contained"
            onClick={() => cleanFilter()}
            sx={{ mt: { xs: 1, sm: 0 } }}
          >
            Limpar
          </Button>
        </Box>

        <DataGrid
          rows={rows() || []}
          columns={columns}
          pageSize={20}
          rowsPerPageOptions={[5]}
          onSortModelChange={(newSort) => {
            const sortField = newSort[0]?.field;
            const sortOrder = newSort[0]?.sort?.toUpperCase();
            setSort(sortField);
            setOrder(parseInt(sortOrder!));
          }}
          sx={{
            boxShadow: 2,
            fontSize: { xs: 12, sm: 14 },
            "& .MuiDataGrid-cell": {
              fontSize: { xs: "12px", sm: "14px" },
              py: { xs: 0.5, sm: 1 },
            },
            "& .MuiDataGrid-columnHeaders": {
              fontSize: { xs: "13px", sm: "16px" },
              whiteSpace: "nowrap",
            },
            "& .MuiDataGrid-row": {
              maxHeight: { xs: "32px", sm: "40px" },
            },
            minHeight: { xs: 200, sm: 300 },
          }}
          hideFooter
        />
        <Box display="flex" justifyContent="center">
          <Pagination
            count={inscricoes?.quantidadePaginas}
            color="primary"
            size="small"
            onChange={(_, page) => {
              setPage(page - 1);
            }}
            page={page + 1}
          />
        </Box>
      </Paper>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "98vw", sm: "90%" },
            height: { xs: "90vh", sm: "80%" },
            p: { xs: 1, sm: 2 },
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              width: "100%",
              gap: { xs: 1, sm: 2 },
              mb: { xs: 1, sm: 0 },
            }}
          >
            <Box component="form" sx={{ width: "100%", mb: { xs: 1, sm: 0 } }}>
              <TextField
                label="Pesquisar por nome ou email"
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
              />
            </Box>
            <FormFilter
              statusEtapa={statusEtapa}
              setStatusEtapa={setStatusEtapa}
              etapa={etapa}
            />
            <Box display="flex" justifyContent="flex-end">
              <IconButton
                onClick={() => setOpenModal(false)}
                title="Retrair tabela"
              >
                <FullscreenExitIcon />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              onClick={() => cleanFilter()}
              size="small"
              sx={{ mt: { xs: 1, sm: 0 } }}
            >
              Limpar
            </Button>
          </Box>

          <DataGrid
            rows={rows() || []}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            sx={{
              flex: 1,
              boxShadow: 2,
              fontSize: { xs: 12, sm: 14 },
              "& .MuiDataGrid-cell": {
                fontSize: { xs: "12px", sm: "14px" },
                py: { xs: 0.5, sm: 1 },
              },
              "& .MuiDataGrid-columnHeaders": {
                fontSize: { xs: "13px", sm: "16px" },
                whiteSpace: "nowrap",
              },
              "& .MuiDataGrid-row": {
                maxHeight: { xs: "32px", sm: "40px" },
              },
              minHeight: { xs: 200, sm: 300 },
            }}
            hideFooter
          />
          <Box display="flex" justifyContent="center">
            <Pagination
              count={inscricoes?.quantidadePaginas}
              color="primary"
              size="small"
              onChange={(_, page) => {
                setPage(page - 1);
              }}
              page={page + 1}
            />
          </Box>
        </Paper>
      </Modal>
    </>
  );
};
